import type { Chip } from "../core/chip";
import type { LayoutPosition } from "../gamebook/projection/projectChips";
import { ChipNode } from "./ChipNode";
import { RoutePath } from "./RoutePath";

type Props = {
  chips: Chip[];
  activeChipId: string;
  positions: Record<string, LayoutPosition>;
  onSelectChip: (id: string) => void;
};

const PAD = 22;
const VW = 720;
const VH = 1000;
const NODE_W = 150;
const NODE_H = 148;
const VIEW_MARGIN = 8;
const viewBox = `${-VIEW_MARGIN} ${-VIEW_MARGIN} ${VW + VIEW_MARGIN * 2} ${VH + VIEW_MARGIN * 2}`;

function stateColor(state: Chip["state"], active: boolean): string {
  if (active) return "var(--cyan)";
  switch (state) {
    case "DANGER":
      return "var(--gold)";
    case "INTRUSION":
      return "var(--purple)";
    case "ENDING":
      return "var(--gold)";
    case "BLOCKED":
      return "rgba(180,180,190,0.55)";
    default:
      return "var(--cyan)";
  }
}

/**
 * Gamebook neighborhood CHIPMAP using shared ChipNode + RoutePath primitives
 * (authoritative octagon/chamfer language) with percent layout positions.
 */
export function GamebookChipMap({
  chips,
  activeChipId,
  positions,
  onSelectChip,
}: Props) {
  const byId = new Map(chips.map((c) => [c.id, c]));
  const edges: { from: string; to: string }[] = [];
  for (const chip of chips) {
    for (const route of chip.routes) {
      if (
        route.kind === "DESTINATION" &&
        route.targetChipId &&
        byId.has(route.targetChipId)
      ) {
        edges.push({ from: chip.id, to: route.targetChipId });
      }
    }
  }

  return (
    <svg
      class="map gamebook-map"
      viewBox={viewBox}
      role="img"
      aria-label="DeckOS Gamebook CHIPMAP"
      preserveAspectRatio="xMidYMid meet"
    >
      <g class="map-atmosphere" opacity="0.35" style={{ pointerEvents: "none" }}>
        <path
          d={`M${PAD} 80 H${VW - PAD} M${PAD} ${VH - 80} H${VW - PAD}
             M80 ${PAD} V${VH - PAD} M${VW - 80} ${PAD} V${VH - PAD}`}
          fill="none"
          stroke="rgba(39,215,255,0.12)"
          stroke-width="1"
        />
      </g>

      {edges.map(({ from, to }) => {
        const a = positions[from];
        const b = positions[to];
        if (!a || !b) return null;
        return (
          <RoutePath
            key={`${from}-${to}`}
            from={{ x: a.x, y: a.y }}
            to={{ x: b.x, y: b.y }}
            color="rgba(39,215,255,0.55)"
            dashed
          />
        );
      })}

      {chips.map((chip) => {
        const pos = positions[chip.id];
        if (!pos) return null;
        const active = chip.id === activeChipId;
        const color = stateColor(chip.state, active);
        return (
          <ChipNode
            key={chip.id}
            chip={{
              ...chip,
              title:
                chip.title.length > 16
                  ? `${chip.title.slice(0, 14)}…`
                  : chip.title,
            }}
            x={pos.x - NODE_W / 2}
            y={pos.y - NODE_H / 2}
            width={NODE_W}
            height={NODE_H}
            active={active}
            color={color}
            onSelect={onSelectChip}
          />
        );
      })}
    </svg>
  );
}
